import createCache from '../../utils/createCache';

/**
 *
 * @param {joki} jokiInstance - instance of the Joki to which this service is linked
 * @param {Object} options - Options for GraphQL Service
 * @param {boolean} [options.mock] - Set to true if no actual requests are sent, and a mock response is returned
 * @param {Object} [options.mockData] - The And object with keys for queries and the data that they should return
 */
export default function createGraphQLService(jokiInstance, options = {}) {
    const serviceId = options.serviceId ? options.serviceId : 'GraphQLService';
    // const serverUrl = options.url ? options.url : process.env.REACT_APP_API_URL;
    // const serverPath = options.path
    //     ? options.path
    //     : process.env.REACT_APP_API_PATH
    //     ? process.env.REACT_APP_API_PATH
    //     : "";

    // if (serverUrl === undefined && options.mock !== true) {
    //     throw new Error(
    //         "No server url is defined in the options nor in the enviromental variables. Set REACT_APP_API_URL in environment."
    //     );
    // }

    // Check validity for mock data options
    if (options.mock !== undefined && options.mock === true) {
        if (options.mockData === undefined) {
            throw new Error('GraphQl mocking is turned on, but no mockData is set.');
        }

        if (options.mockDelay !== undefined && typeof options.mockDelay !== 'number') {
            throw new Error('mockDelay must an integer. It defines the delay in milliseconds before resolving mocked value for the query.');
        }
    }

    const joki = jokiInstance;
    const MOCK = options.mock !== undefined && options.mock === true;
    const Cache = createCache();
    const abortContainer = new Map();

    function jokiEventHandler(event) {
        if (event.to === serviceId) {
            switch (event.key) {
                case 'query':
                    // console.log(event);
                    return cachedQuery(event.body);
                case 'forceQuery':
                    return fetchFromIpaApi(event.body.query || null, event.body.url || null, event.body.abortId || null);
                case 'abortQuery':
                    return abortQuery(event.body);
                default:
                    throw new Error(`${serviceId} received an event with unknown ${event.key}`);
            }
        }
    }

    /**
     * Make GraphQL query using cache.
     * @param {*} eventBody
     */
    function cachedQuery(eventBody) {
        return new Promise((resolve, reject) => {
            const { query, url, abortId } = eventBody;
            const cacheStatus = Cache.status(query);
            switch (cacheStatus) {
                case 'VALID':
                    resolve(Cache.get(query));
                    break;

                case 'FETCHING':
                    Cache.wait(query)
                        .then(results => {
                            resolve(results);
                        })
                        .catch(() => {
                            fetchFromIpaApi(query, url, abortId)
                                .then(results => {
                                    resolve(results);
                                })
                                .catch(err => {
                                    reject(err);
                                });
                        });
                    break;
                case 'INVALID':
                default:
                    fetchFromIpaApi(query, url, abortId)
                        .then(results => {
                            resolve(results);
                        })
                        .catch(err => {
                            reject(err);
                        });
                    break;
            }
        });
    }

    function abortQuery(abortId = null) {
        if (abortId !== null && abortContainer.has(abortId)) {
            abortContainer.get(abortId)();
            abortContainer.delete(abortId);
        }
    }

    function fetchFromIpaApi(queryString, url, abortId = null) {
        // console.log(`FETCH: \n\t${queryString} \n\t${url} \n\t${abortId}`);
        if (typeof url !== 'string') {
            throw new Error('GraphQLService: Query event must provide both the query and the target url.');
        }

        if (MOCK) {
            return mockQuery(queryString);
        }
        return new Promise((resolve, reject) => {
            const authData = requestAuthenticationData();

            if (authData === null) {
                reject('No valid authentication available');
            }

            // const reqId = `${Date.now()}-${Math.round(Math.random() * 100000)}`;
            // const url = `${serverUrl}${serverPath}`;
            Cache.begin(queryString);

            const fetchOptions = {
                method: 'POST',
                body: JSON.stringify({
                    query: queryString,
                }),
                headers: {
                    Authorization: `Bearer ${authData.access_token}`,
                    'Content-Type': 'application/json',
                },
            };

            if (abortId !== null) {
                // console.log(`\tAdd abort id ${abortId} to graphql request ${queryString}`);
                const abContr = new AbortController();
                fetchOptions.signal = abContr.signal;
                abortContainer.set(abortId, () => {
                    // console.log(`\tAbort ${abortId}`);
                    abContr.abort();
                });
            }

            fetch(url, fetchOptions)
                .then(response => response.json())
                .then(results => {
                    if (results.data === undefined && results.errors === undefined) {
                        Cache.del(queryString);
                        if (abortId !== null) {
                            // console.log(`\tGraphQL query Success, delete abort id ${abortId}`);
                            abortContainer.delete(abortId);
                        }

                        // console.warn("GraphQLService:Results/Query/Url:\n", results, "\n\n", queryString, "\n\n", url,"\n\n");
                        reject(`${serviceId}:Error: Results returned from the GraphQL was not in valid format and is missing 'data' key.`);
                    }
                    // If results contain no data but has errors, reject this request.
                    if (results.data === undefined && results.errors !== undefined) {
                        Cache.del(queryString);
                        reject(results.errors);
                    } else {
                        Cache.end(queryString, results.data);
                        Cache.set(queryString, results.data);
                        triggerQuerySuccess(queryString, results.data);
                        resolve(results.data);
                    }
                })
                .catch(err => {
                    Cache.del(queryString);

                    if (err.name !== undefined && err.name === 'AbortError') {
                        // console.log(`\n\nABORTED! ${abortId}`);
                        if (abortId !== null) abortContainer.delete(abortId);
                        reject(err);
                    } else {
                        console.error(`${serviceId}:FETCH:ERROR ${abortId}`, err, err.name);
                        if (abortId !== null) abortContainer.delete(abortId);
                        triggerQueryError(queryString, err);
                        reject(err);
                    }
                });
        });
    }

    function triggerQuerySuccess(query, results) {
        joki.trigger({
            from: serviceId,
            key: 'graphQLQuerySuccess',
            body: {
                query: query,
                results: results,
            },
        });
    }

    function triggerQueryError(query, err) {
        joki.trigger({
            from: serviceId,
            key: 'graphQLQueryError',
            body: {
                query: query,
                error: err,
            },
        });
    }

    /**
     * Request Authentication tokens from AuthenticationService
     */
    function requestAuthenticationData() {
        const response = joki.ask({
            to: 'AuthenticationService',
            key: 'getAuthData',
            syncAsk: true,
        });
        if (response.AuthenticationService !== undefined) {
            return response.AuthenticationService;
        }
        return null;
    }

    function mockQuery(queryString) {
        return new Promise((resolve, reject) => {
            Cache.begin(queryString);
            if (options.mockData[queryString] === undefined) {
                const err = { error: `No results mocked for queryString ${queryString}` };
                Cache.del(queryString);
                triggerQueryError(queryString, err);
                reject(err);
            }
            const mock = options.mockData[queryString];
            const data = typeof mock === 'function' ? mock() : mock;

            const timeOut = options.mockDelay ? options.mockDelay : false;
            if (timeOut) {
                setTimeout(() => {
                    Cache.end(queryString, data);
                    Cache.set(queryString, data);
                    triggerQuerySuccess(queryString, data);
                    resolve(data);
                }, timeOut);
            } else {
                Cache.end(queryString, data);
                Cache.set(queryString, data);
                triggerQuerySuccess(queryString, data);
                resolve(data);
            }
        });
    }

    joki.addService({
        id: serviceId,
        fn: jokiEventHandler,
    });
}
